import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { filterByLabel } from "@/lib/filterByLabel";
import { DropdownService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import { Select, Typography } from "antd";

const { Text } = Typography;

interface SchemaSelectionStepProps {
  selectedSchemaId: string;
  setSelectedSchemaId: (id: string) => void;
  importType: string;
  setImportType: (type: string) => void;
  currentStep: number;
  onContinue: (schemaLabel?: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const SchemaSelectionStep = ({
  selectedSchemaId,
  setSelectedSchemaId,
  importType,
  setImportType,
  currentStep,
  onContinue,
  isLoading = false,
  disabled = false,
}: SchemaSelectionStepProps) => {
  const { data: schemaOptions, isLoading: schemasLoading } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  const handleSchemaSelection = () => {
    if (selectedSchemaId) {
      const selectedSchema = schemaOptions?.find(
        (schema: { value: string; label: string }) =>
          schema.value === selectedSchemaId
      );
      const schemaLabel = selectedSchema?.label ?? selectedSchemaId;
      onContinue(schemaLabel);
    } else {
      notify.warning(
        "No Schema Selected",
        "Please select a schema to import into."
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Text strong>Schema</Text>
          <Select
            placeholder="Choose a schema"
            style={{ width: "100%", marginTop: 8 }}
            value={selectedSchemaId}
            onChange={setSelectedSchemaId}
            options={schemaOptions}
            showSearch
            filterOption={filterByLabel}
            loading={schemasLoading}
            disabled={currentStep > 1}
          />
        </div>
        <div>
          <Text strong>Import Type</Text>
          <Select
            placeholder="Choose import type"
            style={{ width: "100%", marginTop: 8 }}
            value={importType}
            onChange={setImportType}
            options={[
              {
                label: "Create New Records",
                value: "create new records",
              },
              {
                label: "Update Existing Records",
                value: "update existing records",
              },
            ]}
            showSearch
            filterOption={filterByLabel}
            loading={schemasLoading}
            disabled={currentStep > 2}
          />
        </div>
      </div>

      <CustomButton
        type="primary"
        onClick={handleSchemaSelection}
        loading={isLoading}
        disabled={!selectedSchemaId || currentStep > 1 || disabled}
        title="Continue to Next Step"
      >
        Continue
      </CustomButton>
    </div>
  );
};

export default SchemaSelectionStep;
